import { ContentState, convertToRaw, EditorState } from "draft-js";
import { useState, useEffect } from "react";
import { Editor } from "react-draft-wysiwyg";
import ImageUploadButton from "./ImageUploaderButton";
import { db } from "../../../services/firebaseService/connection";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import useAdminDataStore, { selectCurrentGroupId, selectAllGroups } from "../../../store/adminData/adminData";
import { uploadImage } from "../../../services/firebaseService/endPoints/admin/members";
import { getWebsiteConfig } from "../../../services/firebaseService/endPoints/admin/website";
import { ChantButton } from "../../../CommonComponents";
import { doc, setDoc } from "firebase/firestore";
import { postUpdateCDNUtil } from "../../../helperFunctions/util";
import AdminContainer from "../admin-container/AdminContainer";

const fontSize = undefined;

const About = () => {
  const currentGroup = useAdminDataStore(selectCurrentGroupId);
  const allGroups = useAdminDataStore(selectAllGroups);
  const adminData = allGroups[currentGroup];

  const [overview, setOverview] = useState(EditorState.createEmpty());
  const [overviewImage, setOverviewImage] = useState(null);
  const [mission, setMission] = useState(EditorState.createEmpty());
  const [missionImage, setMissionImage] = useState(null);
  const [codeOfConduct, setCodeOfConduct] = useState(EditorState.createEmpty());
  const [codeOfConductImage, setCodeOfConductImage] = useState(null);
  const [ticketSummary, setTicketSummary] = useState(EditorState.createEmpty());
  const [ticketImage, setTicketImage] = useState(null);
  const [bylaws, setBylaws] = useState(EditorState.createEmpty());
  const [imageFileKeys] = useState([
    "overviewImage",
    "missionImage",
    "codeOfConductImage",
    "ticketImage",
    // , 'membershipSummaryImage'
  ]);
  const [isSaving, setIsSaving] = useState(false);
  const [leadershipModal, setLeadershipModal] = useState(undefined); // eslint-disable-line no-unused-vars
  const [leadership, setLeadership] = useState(undefined); // eslint-disable-line no-unused-vars

  const openLeaderSetupModal = () => { // eslint-disable-line no-unused-vars
    setLeadershipModal((prev) => !prev);
  };

  const handleEditorStateChange = (state, name) => {
    if (name === "overview") setOverview(state);
    else if (name === "mission") setMission(state);
    else if (name === "codeOfConduct") setCodeOfConduct(state);
    else if (name === "ticketSummary") setTicketSummary(state);
    else if (name === "bylaws") setBylaws(state);
  };

  const handleSave = (state) => { // eslint-disable-line no-unused-vars
    setLeadership((prev) => ({
      ...prev,
      ...state.leadership,
    }));
  };

  const convertEditorStateToHtml = (editorState) => {
    const contentState = editorState.getCurrentContent();
    const rawContent = convertToRaw(contentState);

    const isEmpty = rawContent.blocks.every(
      (block) => block.text.trim() === "" && block.type === "unstyled"
    );

    if (isEmpty) {
      return "";
    }

    return draftToHtml(rawContent);
  };

  const getState = (websiteConfig) => {
    if (websiteConfig && websiteConfig.about) {
      let overviewState = EditorState.createEmpty();
      let missionState = EditorState.createEmpty();
      let codeOfConductState = EditorState.createEmpty();
      let bylawsState = EditorState.createEmpty();
      let membershipSummary = EditorState.createEmpty(); // eslint-disable-line no-unused-vars
      let ticketSummaryState = EditorState.createEmpty();
      let overviewImageVal = "";
      let missionImageVal = "";
      let codeOfConductImageVal = "";
      let ticketImageVal = "";

      if (websiteConfig.about.overview) {
        let overviewDraft = htmlToDraft(websiteConfig.about.overview);
        if (overviewDraft) {
          let contentState = ContentState.createFromBlockArray(
            overviewDraft.contentBlocks
          );
          let editorState = EditorState.createWithContent(contentState);
          overviewState = editorState;
        }
        overviewImageVal = websiteConfig.about.overviewImage;
      }
      if (websiteConfig.about.mission) {
        let missionDraft = htmlToDraft(websiteConfig.about.mission);
        if (missionDraft) {
          let contentState = ContentState.createFromBlockArray(
            missionDraft.contentBlocks
          );
          let editorState = EditorState.createWithContent(contentState);
          missionState = editorState;
        }
        missionImageVal = websiteConfig.about.missionImage;
      }
      if (websiteConfig.about.codeOfConduct) {
        let codeOfConductDraft = htmlToDraft(websiteConfig.about.codeOfConduct);
        if (codeOfConductDraft) {
          let contentState = ContentState.createFromBlockArray(
            codeOfConductDraft.contentBlocks
          );
          let editorState = EditorState.createWithContent(contentState);
          codeOfConductState = editorState;
        }
        codeOfConductImageVal = websiteConfig.about.codeOfConductImage;
      }

      if (websiteConfig.about.bylaws) {
        let bylawsDraft = htmlToDraft(websiteConfig.about.bylaws);
        if (bylawsDraft) {
          let contentState = ContentState.createFromBlockArray(
            bylawsDraft.contentBlocks
          );
          let editorState = EditorState.createWithContent(contentState);
          bylawsState = editorState;
        }
      }
      if (websiteConfig.about.ticketSummary) {
        let ticketSummaryDraft = htmlToDraft(websiteConfig.about.ticketSummary);
        if (ticketSummaryDraft) {
          let contentState = ContentState.createFromBlockArray(
            ticketSummaryDraft.contentBlocks
          );
          let editorState = EditorState.createWithContent(contentState);
          ticketSummaryState = editorState;
        }
        ticketImageVal = websiteConfig.about.ticketImage;
      }

      return {
        overview: overviewState,
        mission: missionState,
        bylaws: bylawsState,
        codeOfConduct: codeOfConductState,
        ticketSummary: ticketSummaryState,
        ticketImage: ticketImageVal,
        overviewImage: overviewImageVal,
        missionImage: missionImageVal,
        codeOfConductImage: codeOfConductImageVal,
      };
    }
    return {
      overview: EditorState.createEmpty(),
      mission: EditorState.createEmpty(),
      bylaws: EditorState.createEmpty(),
      codeOfConduct: EditorState.createEmpty(),
      membershipSummary: EditorState.createEmpty(),
      ticketSummary: EditorState.createEmpty(),
    };
  };

  const fetchData = () => {
    getWebsiteConfig(currentGroup).then((data) => {
      const s = getState(data);
      if (s.overview !== undefined) setOverview(s.overview);
      if (s.overviewImage !== undefined) setOverviewImage(s.overviewImage);
      if (s.mission !== undefined) setMission(s.mission);
      if (s.missionImage !== undefined) setMissionImage(s.missionImage);
      if (s.codeOfConduct !== undefined) setCodeOfConduct(s.codeOfConduct);
      if (s.codeOfConductImage !== undefined) setCodeOfConductImage(s.codeOfConductImage);
      if (s.ticketSummary !== undefined) setTicketSummary(s.ticketSummary);
      if (s.ticketImage !== undefined) setTicketImage(s.ticketImage);
      if (s.bylaws !== undefined) setBylaws(s.bylaws);
    });
  };

  const saveToFirebase = () => {
    function delay(delayInms) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(2);
        }, delayInms);
      });
    }
    const save = async () => {
      setIsSaving(true);

      let currentState = {
        overview,
        overviewImage,
        mission,
        missionImage,
        codeOfConduct,
        codeOfConductImage,
        ticketSummary,
        ticketImage,
        bylaws,
        imageFileKeys,
      };

      let path = `${adminData.data.groupName}/website/`;
      let imageUploadPromises = [];

      let imageFileKeysArr = currentState.imageFileKeys;
      if (imageFileKeysArr) {
        for (let key of imageFileKeysArr) {
          if (Array.isArray(currentState[key])) {
            let images = currentState[key];
            let index = 0;
            for (let image of images) {
              if (image && (typeof image === "object") & !image.url) {
                imageUploadPromises.push(
                  uploadImage(
                    image,
                    `${path}about/${key}/${index + 1}`,
                    index
                  ).then(async ({ downloadUrl, index }) => {
                    let imageIndex = index;

                    currentState[key][imageIndex] = await downloadUrl;
                  })
                );
                await delay(1000);
              }
              index++;
            }
          } else if (key.includes(".")) {
            let keyParts = key.split(".");
            let file = currentState[keyParts[0]][keyParts[1]];

            if (file && typeof file === "object" && !file.url) {
              imageUploadPromises.push(
                uploadImage(file, `${path}about/${keyParts[0]}`)
                  .then(({ downloadUrl }) => downloadUrl)
                  .then((downloadUrl) => {
                    currentState[keyParts[0]][keyParts[1]] = downloadUrl;
                  })
              );
              await delay(1000);
            }
          } else {
            let image = currentState[key];
            if (image && typeof image === "object" && !image.url) {
              imageUploadPromises.push(
                uploadImage(image, `${path}about/${key}`)
                  .then(({ downloadUrl }) => downloadUrl)
                  .then((downloadUrl) => {
                    currentState[key] = downloadUrl;
                  })
              );
              await delay(1000);
            }
          }
        }
      }

      await Promise.all(imageUploadPromises);
      delete currentState.isSaving;
      delete currentState.imageFileKeys;

      let about = currentState;
      about.overview = convertEditorStateToHtml(about.overview);
      about.mission = convertEditorStateToHtml(about.mission);
      about.codeOfConduct = convertEditorStateToHtml(about.codeOfConduct);
      about.ticketSummary = convertEditorStateToHtml(about.ticketSummary);
      about.bylaws = convertEditorStateToHtml(about.bylaws);

      const gd = adminData.data;
      const identityFields = {
        groupName: gd.groupName || "",
        clubName: gd.clubName || "",
        leagueName: gd.leagueName || "",
        country: gd.country || "",
        ...(gd.clubId ? { clubId: gd.clubId } : {}),
        ...(gd.leagueId ? { leagueId: gd.leagueId } : {}),
      };
      let docRef = doc(db, "website", gd.id);
      setDoc(docRef, { about, ...identityFields }, { merge: true }).then(() => {
        fetchData();
        postUpdateCDNUtil(currentGroup);
        setIsSaving(false);
      });
    };
    save();
  };

  useEffect(() => {
    fetchData();
  }, [currentGroup]); // eslint-disable-line react-hooks/exhaustive-deps

  let primaryColor = "";
  if (
    adminData.data.configuration &&
    adminData.data.configuration.primaryColor
  ) {
    let { r, g, b } = adminData.data.configuration.primaryColor;
    primaryColor = `rgb(${r}, ${g}, ${b})`;
  }

  return (
    <AdminContainer>
      <div className=" p-2">
        <p style={{ fontSize: "20", fontWeight: "bold", marginBottom: 0 }}>
          About
        </p>
        <div className="chant-form-body">
          <div style={{ marginBottom: "16px" }}>
            <label>Overview</label>
            <div style={{ border: "1px solid #ced4da", borderRadius: 5 }}>
              <Editor
                editorState={overview}
                onEditorStateChange={(state) =>
                  handleEditorStateChange(state, "overview")
                }
                toolbar={{
                  options: ["inline", "list", "link", "fontSize"],
                  inline: {
                    inDropdown: false,
                    options: ["bold", "italic", "underline"],
                  },
                  list: {
                    inDropdown: false,
                    options: ["unordered", "ordered"],
                  },
                  fontSize: {
                    icon: fontSize,
                    options: [
                      8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60,
                      72, 96,
                    ],
                    className: undefined,
                    component: undefined,
                    dropdownClassName: undefined,
                  },
                  link: {
                    inDropdown: false,
                    defaultTargetOption: "_self",
                    options: ["link", "unlink"],
                    linkCallback: undefined,
                  },
                }}
                stripPastedStyles={true}
                tabIndex="2"
              />
            </div>
            <ImageUploadButton
              text="Add Image"
              aspectRatio={1}
              images={[overviewImage]}
              onSave={(images) => {
                setOverviewImage(images[0]);
              }}
            />
          </div>
          <div style={{ marginBottom: "16px" }}>
            <label>Mission</label>
            <div style={{ border: "1px solid #ced4da", borderRadius: 5 }}>
              <Editor
                editorState={mission}
                onEditorStateChange={(state) =>
                  handleEditorStateChange(state, "mission")
                }
                toolbar={{
                  options: ["inline", "list", "link", "fontSize"],
                  inline: {
                    inDropdown: false,
                    options: ["bold", "italic", "underline"],
                  },
                  fontSize: {
                    icon: fontSize,
                    options: [
                      8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60,
                      72, 96,
                    ],
                    className: undefined,
                    component: undefined,
                    dropdownClassName: undefined,
                  },
                  list: {
                    inDropdown: false,
                    options: ["unordered", "ordered"],
                  },
                  link: {
                    inDropdown: false,
                    defaultTargetOption: "_self",
                    options: ["link", "unlink"],
                    linkCallback: undefined,
                  },
                }}
                stripPastedStyles={true}
                tabIndex="2"
              />
            </div>
            <ImageUploadButton
              text="Add Image"
              aspectRatio={1}
              images={[missionImage]}
              onSave={(images) => {
                setMissionImage(images[0]);
              }}
            />
          </div>
          <div style={{ marginBottom: "16px" }}>
            <label>Code of Conduct</label>
            <div style={{ border: "1px solid #ced4da", borderRadius: 5 }}>
              <Editor
                editorState={codeOfConduct}
                onEditorStateChange={(state) =>
                  handleEditorStateChange(state, "codeOfConduct")
                }
                toolbar={{
                  options: ["inline", "list", "link", "fontSize"],
                  inline: {
                    inDropdown: false,
                    options: ["bold", "italic", "underline"],
                  },
                  fontSize: {
                    icon: fontSize,
                    options: [
                      8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60,
                      72, 96,
                    ],
                    className: undefined,
                    component: undefined,
                    dropdownClassName: undefined,
                  },
                  list: {
                    inDropdown: false,
                    options: ["unordered", "ordered"],
                  },
                  link: {
                    inDropdown: false,
                    defaultTargetOption: "_self",
                    options: ["link", "unlink"],
                    linkCallback: undefined,
                  },
                }}
                stripPastedStyles={true}
                tabIndex="2"
              />
            </div>
            <ImageUploadButton
              text="Add Image"
              aspectRatio={1}
              images={[codeOfConductImage]}
              onSave={(images) => {
                setCodeOfConductImage(images[0]);
              }}
            />
          </div>
          <div style={{ marginBottom: "16px" }}>
            <label>Bylaws and Minutes</label>
            <div style={{ border: "1px solid #ced4da", borderRadius: 5 }}>
              <Editor
                editorState={bylaws}
                onEditorStateChange={(state) =>
                  handleEditorStateChange(state, "bylaws")
                }
                toolbar={{
                  options: ["inline", "list", "link", "fontSize"],
                  inline: {
                    inDropdown: false,
                    options: ["bold", "italic", "underline"],
                  },
                  fontSize: {
                    icon: fontSize,
                    options: [
                      8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60,
                      72, 96,
                    ],
                    className: undefined,
                    component: undefined,
                    dropdownClassName: undefined,
                  },
                  list: {
                    inDropdown: false,
                    options: ["unordered", "ordered"],
                  },
                  link: {
                    inDropdown: false,
                    defaultTargetOption: "_self",
                    options: ["link", "unlink"],
                    linkCallback: undefined,
                  },
                }}
                stripPastedStyles={true}
                tabIndex="2"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-5 p-2">
        <p style={{ fontSize: "20", fontWeight: "bold", marginBottom: 0 }}>
          Tickets
        </p>
        <div className="chant-form-body">
          <div style={{ marginBottom: "16px" }}>
            <label>Summary</label>
            <div style={{ border: "1px solid #ced4da", borderRadius: 5 }}>
              <Editor
                editorState={ticketSummary}
                onEditorStateChange={(state) =>
                  handleEditorStateChange(state, "ticketSummary")
                }
                toolbar={{
                  options: ["inline", "list", "link", "fontSize"],
                  inline: {
                    inDropdown: false,
                    options: ["bold", "italic", "underline"],
                  },
                  fontSize: {
                    icon: fontSize,
                    options: [
                      8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60,
                      72, 96,
                    ],
                    className: undefined,
                    component: undefined,
                    dropdownClassName: undefined,
                  },
                  list: {
                    inDropdown: false,
                    options: ["unordered", "ordered"],
                  },
                  link: {
                    inDropdown: false,
                    defaultTargetOption: "_self",
                    options: ["link", "unlink"],
                    linkCallback: undefined,
                  },
                }}
                stripPastedStyles={true}
                tabIndex="2"
              />
            </div>
            <ImageUploadButton
              text="Add Image"
              aspectRatio={1}
              images={[ticketImage]}
              onSave={(images) => {
                setTicketImage(images[0]);
              }}
            />
          </div>
        </div>
      </div>
      <div className="d-flex justify-content-center">
        <div className="p-2">
          <ChantButton
            style={{
              backgroundColor: primaryColor,
              borderColor: primaryColor,
              width: "200px",
            }}
            loading={isSaving}
            onClick={saveToFirebase}
          >
            Save
          </ChantButton>
        </div>
      </div>
    </AdminContainer>
  );
};

export default About;
